import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface FAQsAccordionProps {
  faqs: FAQ[];
  expandedFaq: string | null;
  onToggleFaq: (faqId: string) => void;
}

const FAQsAccordion: React.FC<FAQsAccordionProps> = ({
  faqs,
  expandedFaq,
  onToggleFaq,
}) => {
  if (faqs.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p className="text-gray-500 font-manrope">No questions found matching your search.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => {
        const isExpanded = expandedFaq === faq.id;
        
        return (
          <motion.div
            key={faq.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="bg-white border border-[#bdc1ca] rounded-lg overflow-hidden shadow-[0px_0px_2px_rgba(23,26,31,0.12),0px_2px_5px_rgba(23,26,31,0.09)]"
          >
            <button
              onClick={() => onToggleFaq(faq.id)}
              className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <span
                className={`font-open-sans text-xl leading-[30px] pr-4 ${
                  isExpanded ? 'text-[#ffad0a] font-semibold' : 'text-[#565d6d] font-normal'
                }`}
              >
                {faq.question}
              </span>
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                  isExpanded
                    ? 'bg-white border border-[#966400]'
                    : 'bg-white border border-[#bdc1ca]'
                }`}
              >
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180 text-[#966400]' : 'text-[#9095a1]'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 text-brand-yellow font-manrope text-base leading-[26px] border-t border-gray-200 pt-4">
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

export default FAQsAccordion;
